import { api } from '../../../servicesGlobais/api'
import axios from 'axios'
import { Projeto, ProjetoFormData, ProjetoFilters } from '../tipos/projeto'
import { convertFromBackendFormat, convertToBackendFormat } from '../../../adapters/projetoAdapter'
import { cleanFilters, extractErrorMessage } from '../../../utils/formatUtils'

// Interface para resposta paginada
interface PaginatedResponse<T> {
  data: T[]
  success: boolean
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export const projetoService = {
  // Buscar projetos com filtros (método unificado)
  async buscarProjetos(filters: ProjetoFilters = {}): Promise<PaginatedResponse<Projeto>> {
    try {
      const cleanFiltersObj = cleanFilters(filters)
      const queryString = new URLSearchParams(cleanFiltersObj as any).toString()
      const url = `/projeto${queryString ? `?${queryString}` : ''}`
      
      const backendProjetos = await api.get<any[]>(url)
      
      // Normalizar os dados do backend para o formato do frontend
      const projetos = (backendProjetos || []).map((projeto: any) => {
        // Converter códigos de status e prioridade
        // O backend pode retornar como StatusCodigo/PrioridadeCodigo (byte) ou Status.Codigo/Prioridade.Codigo
        const statusCodigo = projeto.StatusCodigo ?? projeto.statusCodigo ?? projeto.Status?.Codigo ?? projeto.status?.codigo
        const prioridadeCodigo = projeto.PrioridadeCodigo ?? projeto.prioridadeCodigo ?? projeto.Prioridade?.Codigo ?? projeto.prioridade?.codigo
        
        return {
          ...projeto,
          id: projeto.Id ?? projeto.id ?? projeto.IdProjeto ?? projeto.idProjeto,
          nome: projeto.Nome ?? projeto.nome ?? '',
          descricao: projeto.Descricao ?? projeto.descricao,
          numeroTicket: projeto.NumeroTicket ?? projeto.numeroTicket,
          statusCodigo: statusCodigo != null ? String(statusCodigo) : '',
          prioridadeCodigo: prioridadeCodigo != null ? String(prioridadeCodigo) : '',
          statusNome: projeto.StatusNome ?? projeto.statusNome ?? projeto.Status?.Descricao ?? projeto.status?.descricao,
          prioridadeNome: projeto.PrioridadeNome ?? projeto.prioridadeNome ?? projeto.Prioridade?.Descricao ?? projeto.prioridade?.descricao,
          tipoProjetoCodigo: projeto.TipoProjetoCodigo ?? projeto.tipoProjetoCodigo != null 
            ? String(projeto.TipoProjetoCodigo ?? projeto.tipoProjetoCodigo) 
            : '',
          dataRecebimento: projeto.DataRecebimento ?? projeto.dataRecebimento ?? '',
          solicitanteId: projeto.SolicitanteId ?? projeto.solicitanteId,
          analistaSMNId: projeto.AnalistaSMNId ?? projeto.analistaSMNId ?? 0,
          analistaDDPId: projeto.AnalistaDDPId ?? projeto.analistaDDPId ?? 0,
          desenvolvedorId: projeto.DesenvolvedorId ?? projeto.desenvolvedorId,
          analistaSMNNome: projeto.AnalistaSMNNome ?? projeto.analistaSMNNome ?? projeto.AnalistaSMN?.Nome ?? projeto.analistaSMN?.nome,
          podeGerarCronograma: projeto.PodeGerarCronograma ?? projeto.podeGerarCronograma ?? false,
          cronograma: projeto.Cronograma ? {
            id: projeto.Cronograma.Id ?? projeto.Cronograma.id,
            projetoId: projeto.Cronograma.ProjetoId ?? projeto.Cronograma.projetoId,
            dataEnvio: projeto.Cronograma.DataEnvio ?? projeto.Cronograma.dataEnvio,
            dataEstimativaInicio: projeto.Cronograma.DataEstimativaInicio ?? projeto.Cronograma.dataEstimativaInicio,
            dataEstimativaFim: projeto.Cronograma.DataEstimativaFim ?? projeto.Cronograma.dataEstimativaFim,
            dataEscopoInicio: projeto.Cronograma.DataEscopoInicio ?? projeto.Cronograma.dataEscopoInicio,
            dataEscopoFim: projeto.Cronograma.DataEscopoFim ?? projeto.Cronograma.dataEscopoFim,
            dataOrcamentoInicio: projeto.Cronograma.DataOrcamentoInicio ?? projeto.Cronograma.dataOrcamentoInicio,
            dataOrcamentoFim: projeto.Cronograma.DataOrcamentoFim ?? projeto.Cronograma.dataOrcamentoFim,
            dataDesenvolvimentoInicio: projeto.Cronograma.DataDesenvolvimentoInicio ?? projeto.Cronograma.dataDesenvolvimentoInicio,
            dataDesenvolvimentoFim: projeto.Cronograma.DataDesenvolvimentoFim ?? projeto.Cronograma.dataDesenvolvimentoFim,
            dataTestesInternosInicio: projeto.Cronograma.DataTestesInternosInicio ?? projeto.Cronograma.dataTestesInternosInicio,
            dataTestesInternosFim: projeto.Cronograma.DataTestesInternosFim ?? projeto.Cronograma.dataTestesInternosFim,
            dataTestesProdutoInicio: projeto.Cronograma.DataTestesProdutoInicio ?? projeto.Cronograma.dataTestesProdutoInicio,
            dataTestesProdutoFim: projeto.Cronograma.DataTestesProdutoFim ?? projeto.Cronograma.dataTestesProdutoFim,
            dataHomologacaoInicio: projeto.Cronograma.DataHomologacaoInicio ?? projeto.Cronograma.dataHomologacaoInicio,
            dataHomologacaoFim: projeto.Cronograma.DataHomologacaoFim ?? projeto.Cronograma.dataHomologacaoFim,
            dataPublicacaoPreProd: projeto.Cronograma.DataPublicacaoPreProd ?? projeto.Cronograma.dataPublicacaoPreProd,
            dataPublicacaoProd: projeto.Cronograma.DataPublicacaoProd ?? projeto.Cronograma.dataPublicacaoProd,
            podeGerarCronograma: projeto.Cronograma.PodeGerarCronograma ?? projeto.Cronograma.podeGerarCronograma ?? false,
          } : undefined,
        } as Projeto
      })
      
      return {
        data: projetos,
        success: true,
        pagination: {
          page: 1,
          limit: 10,
          total: projetos.length,
          totalPages: 1
        }
      }
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar projetos'))
    }
  },

  // Resumo de projetos para o dashboard (total, por status, % vs mês passado)
  async obterResumoDashboard(): Promise<{
    totalAtual: number
    totalMesAnterior: number
    percentualVariacao: number
    resumoPorStatus: Array<{ statusCodigo: number; descricao: string; quantidade: number }>
  }> {
    const res = await api.get<any>('/projeto/dashboard-resumo')
    return {
      totalAtual: res.totalAtual ?? res.TotalAtual ?? 0,
      totalMesAnterior: res.totalMesAnterior ?? res.TotalMesAnterior ?? 0,
      percentualVariacao: res.percentualVariacao ?? res.PercentualVariacao ?? 0,
      resumoPorStatus: (res.resumoPorStatus ?? res.ResumoPorStatus ?? []).map((x: any) => ({
        statusCodigo: x.statusCodigo ?? x.StatusCodigo ?? 0,
        descricao: x.descricao ?? x.Descricao ?? '',
        quantidade: x.quantidade ?? x.Quantidade ?? 0
      }))
    }
  },

  // Projetos recentes para o dashboard (paginado, 5 por página)
  async obterRecentesDashboard(pagina: number = 1, tamanhoPagina: number = 5): Promise<{
    items: Projeto[]
    totalCount: number
    pagina: number
    tamanhoPagina: number
  }> {
    const params = new URLSearchParams({ pagina: String(pagina), tamanhoPagina: String(tamanhoPagina) })
    const res = await api.get<any>(`/projeto/recentes?${params}`)
    const items = (res.items ?? res.Items ?? []).map((projeto: any) => {
      const statusCodigo = projeto.StatusCodigo ?? projeto.statusCodigo ?? projeto.Status?.Codigo
      const prioridadeCodigo = projeto.PrioridadeCodigo ?? projeto.prioridadeCodigo ?? projeto.Prioridade?.Codigo
      const orc = projeto.Orcamento ?? projeto.orcamento
      const valorHoraAtual = projeto.ValorHoraAtual ?? projeto.valorHoraAtual
      return {
        ...projeto,
        id: projeto.Id ?? projeto.id ?? projeto.IdTicket ?? projeto.idTicket,
        nome: projeto.Nome ?? projeto.nome ?? '',
        numeroTicket: projeto.NumeroTicket ?? projeto.numeroTicket,
        statusCodigo: statusCodigo != null ? String(statusCodigo) : '',
        prioridadeCodigo: prioridadeCodigo != null ? String(prioridadeCodigo) : '',
        statusNome: projeto.StatusNome ?? projeto.statusNome ?? projeto.Status?.Descricao,
        prioridadeNome: projeto.PrioridadeNome ?? projeto.prioridadeNome ?? projeto.Prioridade?.Descricao,
        dataRecebimento: projeto.DataRecebimento ?? projeto.dataRecebimento ?? '',
        analistaSMNNome: projeto.AnalistaSMNNome ?? projeto.analistaSMNNome ?? projeto.AnalistaSMN?.Nome,
        analistaDDPNome: projeto.AnalistaDDPNome ?? projeto.analistaDDPNome ?? projeto.AnalistaDDP?.Nome,
        orcamento: orc ? {
          escopoOrcado: orc.EscopoOrcado ?? orc.escopoOrcado,
          desenvolvimentoOrcado: orc.DesenvolvimentoOrcado ?? orc.desenvolvimentoOrcado,
          testesOrcado: orc.TestesOrcado ?? orc.testesOrcado,
          homologacaoCobrado: orc.HomologacaoCobrado ?? orc.homologacaoCobrado
        } : undefined,
        valorHoraAtual: valorHoraAtual != null ? Number(valorHoraAtual) : undefined
      } as Projeto & { orcamento?: { escopoOrcado?: number; desenvolvimentoOrcado?: number; testesOrcado?: number; homologacaoCobrado?: number }, valorHoraAtual?: number }
    })
    return {
      items,
      totalCount: res.totalCount ?? res.TotalCount ?? 0,
      pagina: res.pagina ?? res.Pagina ?? 1,
      tamanhoPagina: res.tamanhoPagina ?? res.TamanhoPagina ?? 5
    }
  },

  // Buscar projeto por ID
  async getProjetoById(id: string): Promise<ProjetoFormData> {
    try {
      const backendData = await api.get(`/projeto/${id}`)
      return convertFromBackendFormat(backendData)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar projeto'))
    }
  },

  // Criar novo projeto
  async criarProjeto(data: ProjetoFormData): Promise<ProjetoFormData> {
    try {
      const backendData = convertToBackendFormat(data)
      const response = await api.post('/projeto', backendData)
      return convertFromBackendFormat(response)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao criar projeto'))
    }
  },

  // Atualizar projeto
  async atualizarProjeto(id: string, data: ProjetoFormData): Promise<ProjetoFormData> {
    try {
      const backendData = convertToBackendFormat(data)
      backendData.Id = parseInt(id)
      const response = await api.put(`/projeto/${id}`, backendData)
      return convertFromBackendFormat(response)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao atualizar projeto'))
    }
  },

  // Excluir projeto
  async excluirProjeto(id: string): Promise<void> {
    try {
      await api.delete(`/projeto/${id}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao excluir projeto'))
    }
  },

  // Buscar projetos para faturamento
  async buscarProjetosFaturamento(filtros: {
    numeroTicket?: string | null
    nomeProjeto?: string | null
    dataInicio: string
    dataFim?: string | null
  }): Promise<any> {
    try {
      const params = new URLSearchParams()
      
      if (filtros.numeroTicket) {
        params.append('numeroTicket', filtros.numeroTicket)
      }
      if (filtros.nomeProjeto) {
        params.append('nomeProjeto', filtros.nomeProjeto)
      }
      if (filtros.dataInicio) {
        params.append('dataInicio', filtros.dataInicio)
      }
      if (filtros.dataFim) {
        params.append('dataFim', filtros.dataFim)
      }
      
      const response = await api.get(`/projeto/buscar-faturamento?${params.toString()}`)
      return response
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar projetos para faturamento'))
    }
  },
  
  // Inserir apontamentos no faturamento (idProjeto para projeto, idSuporte para suporte)
  async inserirApontamento(
    projetoId: number,
    idsSelecionados: number[],
    codigoEtapaFaturamento: number,
    observacao?: string,
    ehSuporte?: boolean
  ): Promise<void> {
    try {
      const payload = ehSuporte
        ? { idProjeto: 0, idSuporte: projetoId, codigoEtapaFaturamento, idsSelecionados, observacao: observacao || null }
        : { idProjeto: projetoId, codigoEtapaFaturamento, idsSelecionados, observacao: observacao || null }
      await api.post('/faturamento/inserir-apontamento', payload)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao inserir apontamentos no faturamento'))
    }
  },


  // Gerar cronograma
  async gerarCronograma(projetoId: number, observacoes: {
    observacaoTesteProduto?: string
    observacaoHomologacao?: string
    observacaoPublicacao?: string
  }): Promise<void> {
    try {
      await api.post(`/projeto/${projetoId}/gerar-cronograma`, observacoes)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao gerar cronograma'))
    }
  },

  // Buscar documentos do projeto
  async buscarDocumentos(projetoId: number): Promise<any[]> {
    try {
      return await api.get(`/documento/projeto/${projetoId}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar documentos'))
    }
  },

  // Download documento
  async downloadDocumento(idDocumento: number): Promise<Blob> {
    try {
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5160/api'
      const token = localStorage.getItem('token')
      const response = await axios.get(`${baseURL}/documento/${idDocumento}/download`, {
        responseType: 'blob',
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        }
      })
      return response.data
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao baixar documento'))
    }
  },

  // Excluir documento
  async excluirDocumento(idDocumento: number): Promise<void> {
    try {
      await api.delete(`/documento/${idDocumento}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao excluir documento'))
    }
  },

  // Buscar tipos de documento
  async buscarTiposDocumento(): Promise<any[]> {
    try {
      return await api.get('/documento/tipos')
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar tipos de documento'))
    }
  },

  // Upload de documento
  async uploadDocumento(projetoId: number, tipoDocumentoCodigo: number, arquivo: File, nomeArquivo: string): Promise<void> {
    try {
      const formData = new FormData()
      formData.append('arquivo', arquivo)
      formData.append('tipoDocumentoCodigo', tipoDocumentoCodigo.toString())
      formData.append('nomeArquivo', nomeArquivo)
      
      await api.post(`/documento/projeto/${projetoId}/upload`, formData)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao enviar documento'))
    }
  },

  // Atualizar documento
  async atualizarDocumento(idDocumento: number, projetoId: number, tipoDocumentoCodigo: number, arquivo: File | undefined, nomeArquivo: string): Promise<void> {
    try {
      const formData = new FormData()
      formData.append('projetoId', projetoId.toString())
      formData.append('tipoDocumentoCodigo', tipoDocumentoCodigo.toString())
      formData.append('nomeArquivo', nomeArquivo)
      
      if (arquivo) {
        formData.append('arquivo', arquivo)
      }
      
      await api.put(`/documento/${idDocumento}`, formData)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao atualizar documento'))
    }
  },
}
